import { useEffect, useState } from "react";
import { ShieldOff } from "lucide-react";
import { api } from "@/lib/api";

const Stat = ({ label, value, tid, tone = "text-slate-100" }) => (
  <div className="rounded-lg border border-slate-800/80 bg-slate-950/40 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`text-xl font-mono-ticker ${tone}`} data-testid={tid}>{value}</div>
  </div>
);

export const KillDigestCard = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/desk/kills/digest?days=7").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  if (err) return null;
  const today = d?.days?.[0];
  return (
    <section className="apx-card p-5 space-y-3" data-testid="desk-kill-digest">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><ShieldOff className="w-4 h-4 text-rose-300" /> Solana paper desk — daily kill digest</h2>
          <p className="text-xs text-slate-400 mt-1">How many new Solana pairs the paper desk scanned and why its Filter rejected them. Anything it cannot verify on-chain is killed, never assumed.</p>
        </div>
        <span className="text-[10px] uppercase tracking-wider rounded-full border border-cyan-800 text-cyan-200 px-2 py-0.5">Paper only · read-only</span>
      </div>
      {!d ? <div className="text-xs text-slate-500" data-testid="desk-kill-digest-loading">Loading digest…</div> : !today ? <div className="text-xs text-slate-500" data-testid="desk-kill-digest-empty">No candidates scanned yet.</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label={`Scanned · ${today.day}`} value={today.scanned} tid="desk-digest-scanned" />
            <Stat label="Killed" value={today.killed} tid="desk-digest-killed" tone="text-rose-200" />
            <Stat label="Passed filter" value={today.passed} tid="desk-digest-passed" tone="text-emerald-200" />
            <Stat label="Verified on-chain" value={today.verified_on_chain} tid="desk-digest-verified" tone="text-cyan-200" />
          </div>
          <ul className="space-y-1" data-testid="desk-digest-gates">
            {today.kills_by_gate.map((g) => (
              <li key={g.gate} className="flex items-center gap-3 text-xs">
                <span className="w-10 text-right font-mono-ticker text-slate-200">{g.count}</span>
                <span className="h-1.5 flex-1 rounded-full bg-slate-800 overflow-hidden"><span className="block h-full rounded-full bg-rose-500/70" style={{ width: `${today.killed ? (g.count / today.killed) * 100 : 0}%` }} /></span>
                <span className="w-56 sm:w-72 text-slate-400 truncate" title={g.label}>{g.label}</span>
              </li>
            ))}
          </ul>
          {d.days.length > 0 && (() => {
            const hist = [...d.days].reverse(); const max = Math.max(1, ...hist.map((x) => x.scanned));
            return (
              <div className="space-y-1" data-testid="desk-digest-history">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Last {hist.length} days · killed <span className="inline-block w-2 h-2 rounded-sm bg-rose-500/70 align-middle" /> passed <span className="inline-block w-2 h-2 rounded-sm bg-emerald-400 align-middle" /></div>
                <div className="flex items-end gap-1.5 h-20" role="img" aria-label="Daily scanned candidates, killed versus passed">
                  {hist.map((x) => (
                    <div key={x.day} className="flex-1 flex flex-col items-center gap-1 min-w-0" data-testid={`desk-digest-bar-${x.day}`} title={`${x.day}: ${x.scanned} scanned · ${x.killed} killed · ${x.passed} passed`}>
                      <span className="text-[10px] font-mono-ticker text-slate-400">{x.scanned}</span>
                      <div className="w-full max-w-[40px] flex flex-col justify-end rounded-sm overflow-hidden bg-slate-800/60" style={{ height: 56 }}>
                        <span className="block w-full bg-emerald-400" style={{ height: `${(x.passed / max) * 56}px` }} />
                        <span className="block w-full bg-rose-500/70" style={{ height: `${(x.killed / max) * 56}px` }} />
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono-ticker">{x.day.slice(5)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          <p className="text-[11px] text-slate-500" data-testid="desk-digest-note">{d.note}</p>
        </>
      )}
    </section>
  );
};
